import { Ellipsis } from "lucide-react";
import {
  Badge, Button, cn,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { MENU_ICON, MENU_PANEL, MENU_ROW } from "../header/menuStyles";
import type { TabKey } from "../tabs";
import { splitForTabBar, type NavBadges, type NavItem } from "./nav";

// No side padding and slightly tightened letters: five labels share as little
// as 360 pixels, and the longest ("Pakkumised") needs every one of its 72.
const ITEM =
  "relative h-full min-w-0 flex-1 flex-col gap-1 rounded-none px-0 text-xs tracking-tight hover:bg-transparent active:scale-100 focus-visible:ring-inset focus-visible:ring-ring [&_svg]:size-5";

/**
 * The portal's navigation on a phone: the services along the bottom edge.
 *
 * The bottom, because that is where a thumb is. The hub wraps its service tabs
 * into rows under the header, which on a phone is three rows of words before
 * the first line of content; this keeps every destination one tap away and
 * gives the whole screen above it to the page.
 *
 * Labels here are the SHORT forms (`portal.nav.*`): five words have to share
 * 360 pixels, and "Hinnapakkumised" alone would take a third of them.
 *
 * It steps aside (`hidden`) while a document page has pinned its own action to
 * the same edge - see `shell/context.ts`.
 */
export function PortalTabBar({
  active,
  nav,
  badges,
  hidden,
  onGo,
}: {
  active: TabKey;
  nav: NavItem[];
  badges: NavBadges;
  hidden: boolean;
  onGo: (tab: TabKey) => void;
}) {
  const t = useT();
  // A bar with one destination is not navigation. A contact with no module
  // open has only the overview, and the wordmark already leads there.
  if (hidden || nav.length < 2) return null;

  const { shown, rest } = splitForTabBar(nav);
  const restActive = rest.some((item) => item.key === active);
  const restCount = rest.reduce((sum, item) => sum + (badges[item.key]?.count ?? 0), 0);

  return (
    <nav
      aria-label={t("portal.group")}
      className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden print:hidden"
    >
      <div className="mx-auto flex h-16 max-w-xl items-stretch">
        {shown.map(({ key, icon: Icon }) => {
          const on = active === key;
          const badge = badges[key];
          return (
            <Button
              key={key}
              variant="ghost"
              aria-current={on ? "page" : undefined}
              onClick={() => onGo(key)}
              className={cn(ITEM, on ? "font-semibold text-primary" : "text-foreground/70")}
            >
              {on && <span className="absolute inset-x-5 top-0 h-0.5 rounded-full bg-primary" />}
              <span className="relative">
                <Icon />
                {badge && badge.count > 0 && (
                  <Badge
                    variant={badge.tone}
                    className="absolute -right-3.5 -top-1.5 bg-card px-1.5 py-0 text-[10px] tabular-nums ring-1 ring-current"
                  >
                    {badge.count}
                    <span className="sr-only"> {badge.label}</span>
                  </Badge>
                )}
              </span>
              <span className="max-w-full truncate">{t(`portal.nav.${key}`)}</span>
            </Button>
          );
        })}

        {rest.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(ITEM, restActive ? "font-semibold text-primary" : "text-foreground/70")}
              >
                {restActive && <span className="absolute inset-x-5 top-0 h-0.5 rounded-full bg-primary" />}
                <span className="relative">
                  <Ellipsis />
                  {restCount > 0 && (
                    <Badge className="absolute -right-3.5 -top-1.5 bg-card px-1.5 py-0 text-[10px] tabular-nums ring-1 ring-current">
                      {restCount}
                    </Badge>
                  )}
                </span>
                <span className="max-w-full truncate">{t("portal.nav.more")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="end" sideOffset={8} collisionPadding={8} className={cn(MENU_PANEL, "w-60")}>
              {rest.map(({ key, icon: Icon }) => {
                const badge = badges[key];
                return (
                  <DropdownMenuItem
                    key={key}
                    onSelect={() => onGo(key)}
                    className={cn(MENU_ROW, "py-2.5 text-sm", active === key && "bg-accent/60 font-semibold")}
                  >
                    <Icon className={MENU_ICON} />
                    <span className="min-w-0 flex-1 truncate">{t(`tabs.${key}`)}</span>
                    {badge && badge.count > 0 && (
                      <Badge variant={badge.tone} className="px-2 tabular-nums">
                        {badge.count}
                        <span className="sr-only"> {badge.label}</span>
                      </Badge>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </nav>
  );
}
